import React from 'react';
import {
    Box,
    Typography,
    Chip,
    LinearProgress,
} from '@mui/material';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import Battery80Icon from '@mui/icons-material/Battery80';
import Battery60Icon from '@mui/icons-material/Battery60';
import Battery30Icon from '@mui/icons-material/Battery30';
import Battery20Icon from '@mui/icons-material/BatteryAlert';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { DeviceState, PairedDevice } from 'api/cancam/types';

interface DeviceStatusProps {
    isConnected: boolean;
    connectedDevice: PairedDevice | null;
    deviceState: DeviceState | null;
}

const getBatteryIcon = (level: number) => {
    if (level >= 80) return <BatteryFullIcon sx={{ color: 'success.main' }} />;
    if (level >= 60) return <Battery80Icon sx={{ color: 'success.light' }} />;
    if (level >= 40) return <Battery60Icon sx={{ color: 'warning.light' }} />;
    if (level >= 20) return <Battery30Icon sx={{ color: 'warning.main' }} />;
    return <Battery20Icon sx={{ color: 'error.main' }} />;
};

const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const DeviceStatus: React.FC<DeviceStatusProps> = ({
    isConnected,
    connectedDevice,
    deviceState,
}) => {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* Connection Status */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {isConnected ? (
                    <Chip
                        icon={<LinkIcon />}
                        label="Connected"
                        size="small"
                        color="success"
                        variant="outlined"
                    />
                ) : (
                    <Chip
                        icon={<LinkOffIcon />}
                        label="Disconnected"
                        size="small"
                        color="error"
                        variant="outlined"
                    />
                )}

                {connectedDevice && (
                    <Typography variant="body2" color="text.secondary">
                        {connectedDevice.deviceName || 'CANcam Device'}
                    </Typography>
                )}
            </Box>

            {/* Patrol connection status */}
            {connectedDevice && (
                <Chip
                    label="Connected to patrol"
                    color="success"
                    size="small"
                />
            )}

            {/* Device State (only shown when device connected) */}
            {connectedDevice && deviceState && (
                <>
                    {/* Battery */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getBatteryIcon(deviceState.battery)}
                        <Typography variant="body2">
                            {deviceState.battery}%
                        </Typography>
                    </Box>

                    {/* Recording Status */}
                    {deviceState.isRecording && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FiberManualRecordIcon
                                sx={{
                                    color: 'error.main',
                                    animation: 'pulse 1s infinite',
                                    '@keyframes pulse': {
                                        '0%': { opacity: 1 },
                                        '50%': { opacity: 0.5 },
                                        '100%': { opacity: 1 },
                                    },
                                }}
                            />
                            <Typography variant="body2" sx={{ color: 'error.main' }}>
                                Recording
                                {deviceState.recordingDuration !== undefined &&
                                    ` - ${formatDuration(deviceState.recordingDuration)}`}
                            </Typography>
                        </Box>
                    )}

                    {/* Pending Uploads */}
                    {deviceState.pendingUploads > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CloudUploadIcon sx={{ color: 'info.main' }} />
                            <Typography variant="body2">
                                {deviceState.pendingUploads} pending upload
                                {deviceState.pendingUploads > 1 ? 's' : ''}
                            </Typography>
                        </Box>
                    )}
                </>
            )}

            {/* No device connected state */}
            {!connectedDevice && isConnected && (
                <Typography variant="body2" color="text.secondary">
                    Generate a pairing code to connect a camera
                </Typography>
            )}
        </Box>
    );
};

export default DeviceStatus;
